// ============================================================================
// cabinet/builtinTabs.ts — встроенные вкладки «Кабинета» (регистрация ядра).
//
// Встроенные вкладки:
//   • «Записи» — приёмы/сессии ядра «ГидраМедик» (medic_encounters): списки
//     «сегодня/предстоящие/прошлые», карточка записи, переход к пациенту и
//     связанным заключениям/тестам через тот же AutoListForm/AutoObjectForm;
//   • «Запись на приём» — свободные окна, запись, перенос, отмена и напоминания
//     (medic_encounters/psy_bookings, права пациента/врача — SSoT сервера);
//   • «Задачи» и «Заявки» — РЕАЛЬНЫЕ списки на существующих RPC
//     (TaskService.ListTasks / OrderService.ListOrders) через общий AutoListForm;
//   • «Арбитраж» — существующие документы базы (маркетплейс: заявки/отклики)
//     через тот же AutoListForm; состав и права приходят с сервера;
//   • «Тестирование» — существующая страница тестов и анкет (PollService);
//   • «Поддержка» — существующий инбокс обращений (SupportService);
//   • «Файлы» — существующий файловый менеджер (ExplorerApp) на RPC FileService;
//   • «Избранное» — существующий чат «только я» (MessengerService/ChatThread);
//   • «Счета/оплаты» — личный счёт на существующих RPC (BillingService/
//     PaymentService): баланс, операции, пополнение.
// Новые вкладки (Результаты и последующие) добавляются поставками/модулями
// через registerCabinetTab — оболочку не меняем.
// ============================================================================

import { createElement } from 'react';
import { CalendarDays, CalendarPlus, ClipboardCheck, ClipboardList, CreditCard, FolderOpen, LifeBuoy, ListChecks, Scale, Star } from 'lucide-react';
import { CabinetAppointmentsTab } from '@/components/cabinet/tabs/CabinetAppointmentsTab';
import { MedicAppointmentsPage } from '@/components/medic/appointments/MedicAppointmentsPage';
import { CabinetArbitrationTab } from '@/components/cabinet/tabs/CabinetArbitrationTab';
import { CabinetBillingTab } from '@/components/cabinet/tabs/CabinetBillingTab';
import { CabinetFavoritesTab } from '@/components/cabinet/tabs/CabinetFavoritesTab';
import { CabinetFilesTab } from '@/components/cabinet/tabs/CabinetFilesTab';
import { CabinetOrdersTab } from '@/components/cabinet/tabs/CabinetOrdersTab';
import { CabinetSupportTab } from '@/components/cabinet/tabs/CabinetSupportTab';
import { CabinetTasksTab } from '@/components/cabinet/tabs/CabinetTasksTab';
import { CabinetTestingTab } from '@/components/cabinet/tabs/CabinetTestingTab';
import { getCabinetTab, registerCabinetTab } from './tabs';

/** Код вкладки поддержки (стабильный; используется для ссылок/настроек). */
export const CABINET_SUPPORT_TAB = 'support';
/** Код вкладки «Записи». */
export const CABINET_APPOINTMENTS_TAB = 'appointments';
/** Код вкладки «Запись на приём» (свободные окна, перенос, отмена, напоминания). */
export const CABINET_APPOINTMENT_BOOKING_TAB = 'appointment_booking';
/** Код вкладки «Задачи». */
export const CABINET_TASKS_TAB = 'tasks';
/** Код вкладки «Заявки». */
export const CABINET_ORDERS_TAB = 'orders';
/** Код вкладки «Арбитраж». */
export const CABINET_ARBITRATION_TAB = 'arbitration';
/** Код вкладки «Тестирование». */
export const CABINET_TESTING_TAB = 'testing';
/** Код вкладки «Файлы». */
export const CABINET_FILES_TAB = 'files';
/** Код вкладки «Избранное». */
export const CABINET_FAVORITES_TAB = 'favorites';
/** Код вкладки «Счета/оплаты». */
export const CABINET_BILLING_TAB = 'billing';

let registered = false;

/** registerBuiltinCabinetTabs — идемпотентная регистрация встроенных вкладок. */
export function registerBuiltinCabinetTabs(): void {
  if (registered) return;
  registered = true;
  if (!getCabinetTab(CABINET_TASKS_TAB)) {
    registerCabinetTab({
      code: CABINET_TASKS_TAB,
      title: 'Задачи',
      icon: ListChecks,
      order: 20,
      description: 'Мои задачи: списки по типам, открытие и создание задач',
      Component: CabinetTasksTab,
    });
  }
  if (!getCabinetTab(CABINET_ORDERS_TAB)) {
    registerCabinetTab({
      code: CABINET_ORDERS_TAB,
      title: 'Заявки',
      icon: ClipboardList,
      order: 30,
      description: 'Мои заявки: списки по типам, открытие и создание заявок',
      Component: CabinetOrdersTab,
    });
  }
  if (!getCabinetTab(CABINET_ARBITRATION_TAB)) {
    registerCabinetTab({
      code: CABINET_ARBITRATION_TAB,
      title: 'Арбитраж',
      icon: Scale,
      order: 40,
      description: 'Материалы для разбора: заявки и отклики маркетплейса (доступ — по правам сервера)',
      Component: CabinetArbitrationTab,
    });
  }
  if (!getCabinetTab(CABINET_TESTING_TAB)) {
    registerCabinetTab({
      code: CABINET_TESTING_TAB,
      title: 'Тестирование',
      icon: ClipboardCheck,
      order: 50,
      description: 'Тесты и анкеты: доступные методики, прохождение и протоколы',
      Component: CabinetTestingTab,
    });
  }
  if (!getCabinetTab(CABINET_SUPPORT_TAB)) {
    registerCabinetTab({
      code: CABINET_SUPPORT_TAB,
      title: 'Поддержка',
      icon: LifeBuoy,
      order: 10,
      description: 'Мои обращения и инбокс специалиста поддержки',
      Component: CabinetSupportTab,
    });
  }
  if (!getCabinetTab(CABINET_APPOINTMENTS_TAB)) {
    registerCabinetTab({
      code: CABINET_APPOINTMENTS_TAB,
      title: 'Записи',
      icon: CalendarDays,
      order: 15,
      description: 'Записи на приём: сегодня/предстоящие/прошлые, карточка и связанные данные',
      Component: CabinetAppointmentsTab,
    });
  }
  if (!getCabinetTab(CABINET_APPOINTMENT_BOOKING_TAB)) {
    registerCabinetTab({
      code: CABINET_APPOINTMENT_BOOKING_TAB,
      title: 'Запись на приём',
      icon: CalendarPlus,
      order: 16,
      description: 'Свободные окна, запись, перенос, отмена и напоминания (пациент/врач)',
      Component: () => createElement(MedicAppointmentsPage, { embedded: true }),
    });
  }
  if (!getCabinetTab(CABINET_FAVORITES_TAB)) {
    registerCabinetTab({
      code: CABINET_FAVORITES_TAB,
      title: 'Избранное',
      icon: Star,
      order: 5,
      description: 'Заметки себе: текст, файлы и пересылка из чатов',
      Component: CabinetFavoritesTab,
    });
  }
  if (!getCabinetTab(CABINET_FILES_TAB)) {
    registerCabinetTab({
      code: CABINET_FILES_TAB,
      title: 'Файлы',
      icon: FolderOpen,
      order: 60,
      description: 'Файлы базы: поиск, загрузка, просмотр и скачивание',
      Component: CabinetFilesTab,
    });
  }
  if (!getCabinetTab(CABINET_BILLING_TAB)) {
    registerCabinetTab({
      code: CABINET_BILLING_TAB,
      title: 'Счета/оплаты',
      icon: CreditCard,
      order: 60,
      description: 'Личный счёт: баланс, история операций и пополнение',
      Component: CabinetBillingTab,
    });
  }
}
